import type { BenefitDetailDto } from "./benefit-types"
import type { EnergyIntakeDraft, EnergyServiceOption } from "./energy-intake"

/** Only bill-editor display/identifier intent, not a second saved application. */
export function captureEnergyBillContext(benefit: BenefitDetailDto) {
  const applicants = benefit.personCollection.filter(person => person.id === benefit.applicantId)
  const applicant = applicants.length === 1 ? applicants[0]! : null
  return {
    hasPseSection: benefit.pseBenefitDetails != null,
    preserveUnreadableSsn: benefit.pseBenefitDetails?.pseBillInformation?.socialSecurityNumber == null,
    applicant: applicant ? { id: applicant.id, lastName: applicant.lastName, firstName: applicant.firstName,
      middleName: applicant.middleName, dateOfBirth: applicant.birthDate?.slice(0, 10) ?? "",
      socialSecurityNumber: benefit.pseBenefitDetails != null ? applicant.socialSecurityNumber ?? "" : "" } : null,
  }
}
export type EnergyBillContext = ReturnType<typeof captureEnergyBillContext>

/** A changed/ambiguous applicant must not change the target of an open Primary intent. */
export function energyBillApplicantCurrent(context: EnergyBillContext, benefit: BenefitDetailDto) {
  return context.applicant != null && benefit.applicantId === context.applicant.id
    && benefit.personCollection.filter(person => person.id === context.applicant!.id).length === 1
}

/** Catalogs remain independent reads. Retain the offered labels/mapping in the draft;
 * never reinterpret selected provider/service or PSE membership after a catalog refresh.
 * Unanswered rows remain legal for the existing partial-intake request.
 */
export function energyServiceChoicesCurrent(draft: EnergyIntakeDraft, captured: readonly EnergyServiceOption[], current: readonly EnergyServiceOption[]) {
  const ids = [...draft.services.map(service => service.id),
    ...(draft.liheapEnabled ? [draft.liheap.primaryServiceProviderServiceId, draft.liheap.secondaryServiceProviderServiceId] : [])]
  return ids.filter(id => id !== "").every(id => {
    const offered = captured.filter(option => String(option.id) === id)
    const now = current.filter(option => String(option.id) === id)
    return offered.length === 1 && now.length === 1 && offered[0]!.serviceId === now[0]!.serviceId
      && offered[0]!.providerId === now[0]!.providerId && offered[0]!.isActive === now[0]!.isActive
      && (offered[0]!.isPseEligible === true) === (now[0]!.isPseEligible === true)
  })
}
